import { Controller } from "@hotwired/stimulus";
import { CalendarScheduler } from "../calendar";
import { buildSampleSeed } from "../scheduler_playground/sample_data";

// Host controller for the calendar playground.
// Owns in-memory state and wires the calendar's events to JS-only mutations
// + UI affordances (modal create/edit, theme switcher, sample picker, etc.).
//
// This file is the canonical example of "I want a fully in-memory calendar":
// when someone asks how to consume CalendarScheduler from the host side, point
// them here. The calendar package itself stays plain JS — all UI flavor below
// is host concern.
export default class extends Controller {
  static targets = [
    "calendar",
    "dateDisplay",
    "dayViewBtn",
    "weekViewBtn",
    "monthViewBtn",
    // Polish controls
    "sampleSelect",
    "themeSelect",
    "slotHeightSlider",
    "slotHeightLabel",
    "staffFilter",
    // Create modal
    "createModal",
    "createModalPanel",
    "createNameInput",
    "createStaffSelect",
    "createStartInput",
    "createEndInput",
    // Edit modal
    "editModal",
    "editModalPanel",
    "editNameInput",
    "editStaffSelect",
    "editStartInput",
    "editEndInput",
  ];

  connect() {
    this.date = this._todayString();
    this.view = "day";
    this.sampleKey = "small_org";
    this.state = buildSampleSeed(this.sampleKey, this.date);
    this.hiddenStaffIds = new Set();

    this.calendar = new CalendarScheduler({
      element: this.calendarTarget,
      date: this.date,
      view: this.view,
      staff: this.state.staff,
      events: this.state.events,
      availableViews: ["day", "week", "month"],
    });

    this._bindCalendarEvents();
    this._renderDateDisplay();
    this._updateViewToggle();
    this._renderStaffFilter();
  }

  disconnect() {
    this.calendar?.destroy();
  }

  // ---- view toggle ----

  switchView(event) {
    const newView = event.currentTarget.dataset.view;
    if (newView === this.view) return;
    this.view = newView;
    this.calendar.setView(newView);
    this._renderDateDisplay();
    this._updateViewToggle();
    this._renderStaffFilter();
  }

  _updateViewToggle() {
    [
      [this.dayViewBtnTarget, "day"],
      [this.weekViewBtnTarget, "week"],
      [this.monthViewBtnTarget, "month"],
    ].forEach(([btn, view]) => {
      const active = view === this.view;
      btn.classList.toggle("bg-white", active);
      btn.classList.toggle("shadow-sm", active);
      btn.classList.toggle("text-black", active);
      btn.classList.toggle("text-gray-600", !active);
    });
  }

  // ---- date nav ----

  previousPeriod() {
    this._shiftDate(-1);
  }

  nextPeriod() {
    this._shiftDate(+1);
  }

  today() {
    this._setDate(this._todayString());
  }

  _shiftDate(direction) {
    const d = this._parseLocalDate(this.date);
    if (this.view === "month") {
      d.setMonth(d.getMonth() + direction);
    } else if (this.view === "week") {
      d.setDate(d.getDate() + direction * 7);
    } else {
      d.setDate(d.getDate() + direction);
    }
    this._setDate(this._formatDate(d));
  }

  _setDate(newDate) {
    if (newDate === this.date) return;
    this.date = newDate;
    this.calendar.setDate(newDate);
    this._renderDateDisplay();
  }

  _renderDateDisplay() {
    if (!this.hasDateDisplayTarget) return;
    const d = this._parseLocalDate(this.date);
    if (this.view === "month") {
      this.dateDisplayTarget.textContent = d.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
    } else if (this.view === "week") {
      const days = this._weekRange(this.date);
      const start = days[0];
      const end = days[6];
      const sameMonth = start.getMonth() === end.getMonth();
      const startStr = start.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      const endStr = sameMonth
        ? end.toLocaleDateString("en-US", { day: "numeric", year: "numeric" })
        : end.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
      this.dateDisplayTarget.textContent = `${startStr} – ${endStr}`;
    } else {
      this.dateDisplayTarget.textContent = d.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }
  }

  _weekRange(dateString) {
    const anchor = this._parseLocalDate(dateString);
    const dayOfWeek = anchor.getDay();
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const monday = new Date(
      anchor.getFullYear(),
      anchor.getMonth(),
      anchor.getDate() - daysFromMonday
    );
    return Array.from({ length: 7 }, (_, i) =>
      new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i)
    );
  }

  // ---- polish controls ----

  changeSample(event) {
    this.sampleKey = event.currentTarget.value;
    this.hiddenStaffIds.clear();
    this.state = buildSampleSeed(this.sampleKey, this.date);
    this._pushDataToCalendar();
    this._renderStaffFilter();
  }

  changeTheme(event) {
    const theme = event.currentTarget.value;
    if (theme === "default") {
      delete this.element.dataset.theme;
    } else {
      this.element.dataset.theme = theme;
    }
    // Theme variables changed — refresh layout math via re-paint.
    this.calendar.refresh();
  }

  changeSlotHeight(event) {
    const px = parseInt(event.currentTarget.value, 10);
    // Setting the variable on the playground container would be shadowed by
    // calendar.css's explicit rule on `[data-calendar-root]`. Inline style
    // on the calendar root itself wins.
    this.calendarTarget.style.setProperty("--calendar-slot-height", `${px}px`);
    if (this.hasSlotHeightLabelTarget) {
      this.slotHeightLabelTarget.textContent = `${px}px`;
    }
    // JS positioning math reads the same CSS variable; refresh re-reads it.
    this.calendar.refresh();
  }

  reset() {
    this.hiddenStaffIds.clear();
    this.state = buildSampleSeed(this.sampleKey, this.date);
    this._pushDataToCalendar();
    this._renderStaffFilter();
  }

  // ---- staff filter (Day view only) ----

  _renderStaffFilter() {
    if (!this.hasStaffFilterTarget) return;
    // Filter applies in all views — Day shows visible columns; Week/Month
    // filter the events from hidden staff.
    const showFilter = this.state.staff.length > 0;
    this.staffFilterTarget.hidden = !showFilter;
    this.staffFilterTarget.replaceChildren();
    if (!showFilter) return;

    const heading = document.createElement("span");
    heading.className = "text-xs text-gray-500 font-medium mr-1 self-center";
    heading.textContent = "Staff:";
    this.staffFilterTarget.appendChild(heading);

    this.state.staff.forEach((member) => {
      const wrapper = document.createElement("label");
      wrapper.className =
        "flex items-center gap-1 cursor-pointer select-none";
      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      checkbox.checked = !this.hiddenStaffIds.has(String(member.id));
      checkbox.dataset.staffId = String(member.id);
      checkbox.addEventListener("change", () =>
        this._toggleStaff(String(member.id), checkbox.checked)
      );
      const text = document.createElement("span");
      text.className = "text-xs text-gray-700";
      text.textContent = member.name;
      wrapper.append(checkbox, text);
      this.staffFilterTarget.appendChild(wrapper);
    });
  }

  _toggleStaff(staffId, isVisible) {
    if (isVisible) {
      this.hiddenStaffIds.delete(staffId);
    } else {
      this.hiddenStaffIds.add(staffId);
    }
    this._pushDataToCalendar();
  }

  // Funnel for setData — applies the staff filter on the way out so the
  // calendar only receives staff/events the user wants visible.
  _pushDataToCalendar() {
    const visibleStaff = this.state.staff.filter(
      (s) => !this.hiddenStaffIds.has(String(s.id))
    );
    const visibleStaffIds = new Set(visibleStaff.map((s) => String(s.id)));
    const visibleEvents = this.state.events.filter((e) =>
      visibleStaffIds.has(String(e.staffId))
    );
    this.calendar.setData({ staff: visibleStaff, events: visibleEvents });
  }

  // ---- calendar event wiring (the canonical recipe) ----

  _bindCalendarEvents() {
    this.calendar.on("slot-click", (e) => this._onSlotClick(e.detail));
    this.calendar.on("event-click", (e) => this._onEventClick(e.detail));
    this.calendar.on("date-cell-click", (e) =>
      this._onDateCellClick(e.detail)
    );
    this.calendar.on("event-move", (e) => this._onEventMove(e.detail));
    this.calendar.on("event-resize", (e) => this._onEventResize(e.detail));
  }

  // Drag-to-move landed: the calendar already moved the node visually + emitted
  // the event. Persist into in-memory state so the new position survives a
  // repaint or a view switch.
  _onEventMove({ id, newStart, newEnd, newStaffId }) {
    const event = this._findEvent(id);
    if (!event) return;
    event.start = newStart;
    event.end = newEnd;
    if (newStaffId) event.staffId = String(newStaffId);
    this._pushDataToCalendar();
  }

  // Resize landed: persist the new end time. start stays unchanged.
  _onEventResize({ id, newEnd }) {
    const event = this._findEvent(id);
    if (!event) return;
    event.end = newEnd;
    this._pushDataToCalendar();
  }

  // Month-view drill-down: clicking a day cell switches to Day view at that
  // date. The seed is NOT regenerated — empty cells stay empty after drill-in.
  _onDateCellClick({ date }) {
    this.view = "day";
    this.date = date;
    this.calendar.setView("day");
    this.calendar.setDate(date);
    this._renderDateDisplay();
    this._updateViewToggle();
    this._renderStaffFilter();
  }

  // ---- create flow ----

  _onSlotClick({ staffId, startTime, endTime, clientX, clientY }) {
    this._pendingSlot = { staffId, startTime, endTime };

    this._populateStaffSelect(this.createStaffSelectTarget);
    this.createStaffSelectTarget.value = staffId
      ? String(staffId)
      : String(this.state.staff[0]?.id || "");
    this.createNameInputTarget.value = "";
    this.createStartInputTarget.value = this._timeOfDay(startTime);
    this.createEndInputTarget.value = this._timeOfDay(endTime);

    this._showModal(this.createModalTarget, this.createModalPanelTarget, clientX, clientY);
    requestAnimationFrame(() => this.createNameInputTarget.focus());
  }

  submitCreate(event) {
    event.preventDefault();
    if (!this._pendingSlot) return;

    const formData = new FormData(event.currentTarget);
    const clientName = String(formData.get("clientName") || "").trim();
    if (!clientName) return;

    const staffId = String(formData.get("staffId"));
    const startTime = formData.get("startTime");
    const endTime = formData.get("endTime");
    const baseDate = this._pendingSlot.startTime.split("T")[0];
    const start = `${baseDate}T${startTime}:00`;
    const end = `${baseDate}T${endTime}:00`;

    this.state.events.push({
      id: `pg-${Date.now()}`,
      kind: "appointment",
      start,
      end,
      status: "confirmed",
      staffId,
      durationMinutes: this._minutesBetween(start, end),
      bufferMinutes: 0,
      bodyHtml: this._eventBodyHtml(clientName, "New appointment"),
    });
    this._pushDataToCalendar();
    this._closeCreateModal();
  }

  closeCreateModal() {
    this._closeCreateModal();
  }

  backdropCloseCreate(event) {
    if (event.target === this.createModalTarget) this._closeCreateModal();
  }

  _closeCreateModal() {
    this._hideModal(this.createModalTarget);
    this._pendingSlot = null;
  }

  // ---- edit flow ----

  _onEventClick({ id, kind, clientX, clientY }) {
    if (kind !== "appointment") {
      window.alert("Calendar blocks are read-only in this playground.");
      return;
    }
    const event = this._findEvent(id);
    if (!event) return;
    this._editingId = String(id);

    const clientName = this._extractClientName(event.bodyHtml);
    this._populateStaffSelect(this.editStaffSelectTarget);
    this.editNameInputTarget.value = clientName;
    this.editStaffSelectTarget.value = String(event.staffId);
    this.editStartInputTarget.value = this._timeOfDay(event.start);
    this.editEndInputTarget.value = this._timeOfDay(event.end);

    this._showModal(this.editModalTarget, this.editModalPanelTarget, clientX, clientY);
    requestAnimationFrame(() => this.editNameInputTarget.focus());
  }

  submitEdit(event) {
    event.preventDefault();
    if (!this._editingId) return;

    const target = this._findEvent(this._editingId);
    if (!target) {
      this._closeEditModal();
      return;
    }

    const formData = new FormData(event.currentTarget);
    const clientName = String(formData.get("clientName") || "").trim();
    if (!clientName) return;

    const staffId = String(formData.get("staffId"));
    const startTime = formData.get("startTime");
    const endTime = formData.get("endTime");
    const baseDate = String(target.start).split("T")[0];
    const start = `${baseDate}T${startTime}:00`;
    const end = `${baseDate}T${endTime}:00`;

    target.staffId = staffId;
    target.start = start;
    target.end = end;
    target.durationMinutes = this._minutesBetween(start, end);
    target.bodyHtml = this._eventBodyHtml(clientName, "Edited");

    this._pushDataToCalendar();
    this._closeEditModal();
  }

  deleteFromEditModal() {
    if (!this._editingId) return;
    this.state.events = this.state.events.filter(
      (e) => String(e.id) !== String(this._editingId)
    );
    this._pushDataToCalendar();
    this._closeEditModal();
  }

  closeEditModal() {
    this._closeEditModal();
  }

  backdropCloseEdit(event) {
    if (event.target === this.editModalTarget) this._closeEditModal();
  }

  _closeEditModal() {
    this._hideModal(this.editModalTarget);
    this._editingId = null;
  }

  stopPropagation(event) {
    event.stopPropagation();
  }

  // ---- modal helpers ----

  _showModal(modal, panel, clientX, clientY) {
    modal.classList.remove("hidden");
    document.body.style.overflow = "hidden";
    if (panel && Number.isFinite(clientX) && Number.isFinite(clientY)) {
      this._positionPanel(panel, clientX, clientY);
    }
  }

  _hideModal(modal) {
    modal.classList.add("hidden");
    document.body.style.overflow = "";
  }

  // Place the modal panel near (clientX, clientY) with a small offset, then
  // clamp inside the viewport so the whole panel stays visible.
  _positionPanel(panel, clientX, clientY) {
    const offset = 8;
    const margin = 8;
    // Reset any prior positioning so getBoundingClientRect reads natural size.
    panel.style.left = "0px";
    panel.style.top = "0px";
    const rect = panel.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = clientX + offset;
    let top = clientY + offset;
    if (left + rect.width + margin > vw) left = vw - rect.width - margin;
    if (top + rect.height + margin > vh) top = vh - rect.height - margin;
    if (left < margin) left = margin;
    if (top < margin) top = margin;

    panel.style.left = `${left}px`;
    panel.style.top = `${top}px`;
  }

  _populateStaffSelect(select) {
    select.replaceChildren();
    this.state.staff.forEach((member) => {
      const option = document.createElement("option");
      option.value = String(member.id);
      option.textContent = member.name;
      select.appendChild(option);
    });
  }

  // ---- helpers ----

  _findEvent(id) {
    return this.state.events.find((e) => String(e.id) === String(id));
  }

  _timeOfDay(iso) {
    return String(iso).split("T")[1]?.slice(0, 5) || "09:00";
  }

  _minutesBetween(startIso, endIso) {
    const m = (iso) => {
      const [, time] = iso.split("T");
      const [h, mm] = time.split(":").map(Number);
      return h * 60 + mm;
    };
    return Math.max(0, m(endIso) - m(startIso));
  }

  _eventBodyHtml(client, sub) {
    return `<p class="text-sm font-bold text-black">${this._escape(client)}</p><p class="text-xs text-gray-700">${this._escape(sub)}</p>`;
  }

  // Best-effort: pull the first text node from bodyHtml as a guess of "client name".
  // Good enough for the playground; real consumers would store this structured.
  _extractClientName(bodyHtml) {
    if (!bodyHtml) return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = bodyHtml;
    return (tmp.textContent || "").trim().split("\n")[0]?.trim() || "";
  }

  _todayString() {
    return this._formatDate(new Date());
  }

  _parseLocalDate(dateString) {
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  _formatDate(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  _escape(value) {
    const div = document.createElement("div");
    div.textContent = String(value);
    return div.innerHTML;
  }
}
